/**
 * The Reports page: five reports over one window.
 *
 * One page with tabs rather than five sidebar entries, which is the shape Perfex
 * uses. The window is the reason: all five answer the same question ("what did
 * this period look like") from five angles, and a range picked on one of them has
 * to still be in force when the next one opens. Five separate pages would each
 * carry their own copy of the range and drift the first time somebody switched.
 *
 * The tab is in the URL query (`?tab=`) rather than in a route segment, for the
 * same reason: a route change would remount the section and lose the range. The
 * range is deliberately NOT in the URL - it is a working state, not an address,
 * and a shareable link to "the last twelve months" would mean something different
 * every month it was opened.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import {
  getExpensesReport,
  getLeadsReport,
  getProfitReport,
  getSalesReport,
  getTimesheetsReport,
  REPORT_FOR_TAB,
  type ExpensesReport,
  type LeadsReport,
  type ProfitReport,
  type SalesReport,
  type TimesheetsReport,
} from "../../../lib/reports";
import { PageHead, PillTabs } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ExportMenu } from "./ExportMenu";
import { RangeBar, presetRange, type Range } from "./RangeBar";
import { SalesReportTab } from "./SalesReportTab";
import { ExpensesReportTab } from "./ExpensesReportTab";
import { ProfitReportTab } from "./ProfitReportTab";
import { LeadsReportTab } from "./LeadsReportTab";
import { TimesheetsReportTab } from "./TimesheetsReportTab";

const TABS = ["sales", "expenses", "profit", "leads", "time"] as const;
type Tab = (typeof TABS)[number];

/** Whatever came back for the tab currently open. A union rather than five
 *  states, so switching tabs cannot leave the previous report's numbers under
 *  the new report's headings. */
type Payload =
  | { tab: "sales"; data: SalesReport }
  | { tab: "expenses"; data: ExpensesReport }
  | { tab: "profit"; data: ProfitReport }
  | { tab: "leads"; data: LeadsReport }
  | { tab: "time"; data: TimesheetsReport };

function isTab(v: string | null): v is Tab {
  return !!v && (TABS as readonly string[]).includes(v);
}

export function ReportsSection({ user: _user }: { user: StaffUser }) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const tab: Tab = isTab(params.get("tab")) ? (params.get("tab") as Tab) : "sales";
  const [range, setRange] = useState<Range>(() => presetRange("12m"));
  const [payload, setPayload] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setFailed(false);
      try {
        if (tab === "sales") setPayload({ tab, data: await getSalesReport(range, signal) });
        else if (tab === "expenses") setPayload({ tab, data: await getExpensesReport(range, signal) });
        else if (tab === "profit") setPayload({ tab, data: await getProfitReport(range, signal) });
        else if (tab === "leads") setPayload({ tab, data: await getLeadsReport(range, signal) });
        else setPayload({ tab, data: await getTimesheetsReport(range, signal) });
      } catch {
        // An aborted request is the ordinary case here - every keystroke in the
        // date field starts a new one - so a failure only becomes an error
        // message once the request that failed is still the current one.
        if (!signal.aborted) {
          setPayload(null);
          setFailed(true);
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [tab, range],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return (
    <PopHost>
      <PageHead title={t("reports.title")} sub={t("reports.intro")} />

      <PillTabs
        rail
        className="mb-4"
        value={tab}
        onChange={(v) => {
          // `replace`, so five tabs do not become five entries in the back stack
          // between the page the operator came from and the page they leave to.
          setParams({ tab: v }, { replace: true });
        }}
        tabs={TABS.map((v) => ({ value: v, label: t(`reports.tab.${v}`) }))}
      />

      {/* The export is handed the tab and the window, and nothing else. It
          re-asks the server for the same report rather than serialising what is
          already on screen, so a file cannot be built from a stale payload that
          is still fading out behind a slower one. */}
      <RangeBar
        range={range}
        onChange={setRange}
        actions={<ExportMenu report={REPORT_FOR_TAB[tab]} range={range} />}
      />

      {loading && !payload ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : null}

      {failed ? <p className="crm-rep-note">{t("common.error")}</p> : null}

      {/* The previous answer stays on screen while the next one loads, dimmed.
          Blanking the page on every range change would make a two-second query
          look like a page that broke. */}
      {payload ? (
        <div
          className="crm-rep-body"
          style={loading ? { opacity: 0.55, transition: "opacity .15s ease" } : undefined}
        >
          {payload.tab === "sales" ? <SalesReportTab data={payload.data} /> : null}
          {payload.tab === "expenses" ? <ExpensesReportTab data={payload.data} /> : null}
          {payload.tab === "profit" ? <ProfitReportTab data={payload.data} /> : null}
          {payload.tab === "leads" ? <LeadsReportTab data={payload.data} /> : null}
          {payload.tab === "time" ? <TimesheetsReportTab data={payload.data} /> : null}
        </div>
      ) : null}
    </PopHost>
  );
}
